import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTTable from '../common/Table';
import { apiGet, apiPost } from '../../api/apiClient';
import '../common/rtCommon.css';
import './ViewBuildings.css';
import { toastConfirm, toastSuccess, toastError } from '../common/toast';

const ViewBuildings = () => {
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeVacatedBuildings, setIncludeVacatedBuildings] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ includeVacatedBuildings: false });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchBuildings(); // Fetch buildings on component mount
  }, []);

  const fetchBuildings = async (filters = appliedFilters) => {
    setLoading(true);
    setError(null); // Clear any existing error
    try {
      const url = filters.includeVacatedBuildings
        ? '/building/list?includeVacatedBuildings=true'
        : '/building/list';
      const res = await apiGet(url);
      setBuildings(res.data || []);
    } catch (err) {
      setError('Failed to load buildings. Please try again.');
      toastError('Error fetching buildings.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    const filters = { includeVacatedBuildings };
    setAppliedFilters(filters);
    fetchBuildings(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = { includeVacatedBuildings: false };
    setIncludeVacatedBuildings(false);
    setAppliedFilters(defaultFilters);
    fetchBuildings(defaultFilters);
  };

  const handleVacateBuilding = async (buildingId) => {
    const confirmVacate = await toastConfirm(true, 'Are you sure you want to vacate this building?');
    if (confirmVacate) {
      try {
        const res = await apiPost(`/building/vacate/${buildingId}`);
        if (res.data.status === 'success') {
          toastSuccess('Vacated Building Successfully'); // Show success toast
          fetchBuildings(); // Refresh the building list
        }
      } catch (err) {
        toastError(err.response?.data?.detail || 'Failed to vacate building.');
      }
    }
  };

  const handleViewBuilding = (buildingId) => {
    navigate(`/building/view/${buildingId}`);
  };

  const columns = [
    { Header: 'Building Name', accessor: 'name' },
    { Header: 'Address', accessor: 'address' },
    { 
      Header: 'Rooms', 
      accessor: 'room_count',
      Cell: (cell) => cell.value || 0
    },
    { 
      Header: 'Beds (Occupied/Total)', 
      accessor: 'bed_occupancy',
      Cell: ({ row }) => {
        const occupied = row.occupied_beds || 0;
        const total = row.total_beds || 0;
        return `${occupied} / ${total}`;
      }
    },
    { Header: 'Type', accessor: 'type' },
    { Header: 'Rent', accessor: 'rent', Cell: (cell) => `\u20B9 ${cell.value}` },
    { Header: 'Status', accessor: 'status' },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: (cell) => {
        const building = buildings.find((b) => b.id === cell.value); // Find the building by ID
        return (
          <div className="">
            {building?.status === 'Active' && (
              <RTButton
                type="link"
                onClick={() => handleVacateBuilding(cell.value)}
                style={{ marginRight: '10px' }}
              >
                Vacate Building
              </RTButton>
            )}
            <RTButton
              type="link"
              onClick={() => handleViewBuilding(cell.value)}
            >
              View
            </RTButton>
          </div>
        );
      },
    },
  ];

  if (loading) return <RTSpinner fullScreen={true} />;

  return (
    <div className="rt-layout">
      {/* Left Menu */}
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        {/* Title Row */}
        <div className="rt-title-row">
          <h2 className="rt-title">View Buildings</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={() => navigate(-1)}>
              &#8592; Back
            </RTButton>
          </div>
        </div>
        {/* Main Section */}
        <div className="rt-main-section">
          {/* Custom Filter Section */}
          <div className="building-filter-container">
            <div className="building-filter-content">
              <div className="building-filter-row">
                <div className="building-filter-checkbox-group">
                  <label className="building-filter-checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeVacatedBuildings}
                      onChange={(e) => setIncludeVacatedBuildings(e.target.checked)}
                      className="building-filter-checkbox"
                    />
                    <span className="building-filter-checkbox-text">Include Vacated Buildings</span>
                  </label>
                </div>
                <div className="building-filter-actions">
                  <RTButton
                    variant="secondary"
                    onClick={handleClearFilters}
                    className="building-filter-clear-btn"
                  >
                    Clear Filters
                  </RTButton>
                  <RTButton
                    variant="primary"
                    onClick={handleApplyFilters}
                    className="building-filter-apply-btn"
                  >
                    Apply Filters
                  </RTButton>
                </div>
              </div>
            </div>
          </div>
          {error ? (
            <div className="rt-error-container">
              <div className="rt-error-message">{error}</div>
              <RTButton variant="primary" onClick={fetchBuildings} style={{ marginTop: '20px' }}>
                Retry
              </RTButton>
            </div>
          ) : (
            <RTTable
              columns={columns}
              data={buildings}
              showPagination={true}
              showSearch={true}
              exportable={false}
              noDataText="No buildings found."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewBuildings;